import type { ReactNode } from "react";
import { Download, RotateCcw } from "lucide-react";
import { Modal } from "../Modal";
import type { LedgerPayment, PaymentAllocation } from "../../services/financialService";
import { paymentMethodLabel, paymentStatusLabel, PAYMENT_STATUS_DOTS } from "./paymentRegister";

const money = (value: string | number | undefined | null) => `UGX ${Number(value || 0).toLocaleString()}`;
const when = (value?: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

/** One receipt in full: who paid, how, where the money went, and what can still be done with it. */
export function PaymentDetailsModal({
  payment,
  onClose,
  onDownloadReceipt,
  onRequestRefund,
  onReallocate,
  refundPending,
  children,
}: {
  payment: LedgerPayment | null;
  onClose: () => void;
  /** Each action is omitted when it doesn't apply to this receipt or this user. */
  onDownloadReceipt?: () => void;
  onRequestRefund?: () => void;
  onReallocate?: (allocation: PaymentAllocation) => void;
  /** A refund request for this receipt is already waiting for approval. */
  refundPending?: boolean;
  /** Shown at the end, e.g. the refund request form once asked for. */
  children?: ReactNode;
}) {
  if (!payment) return null;

  const title = payment.receipt_number ? `Receipt ${payment.receipt_number}` : `Payment from ${payment.student_name}`;
  const facts: [string, string][] = [
    ["Student", payment.student_name],
    ["Paid for", payment.fee_name || "PRN receipt"],
    ["Method", paymentMethodLabel(payment.payment_method)],
    ["Reference", payment.reference_number || "—"],
    [
      "Received into",
      payment.receipt_account_name
        ? `${payment.receipt_account_code ? `${payment.receipt_account_code} · ` : ""}${payment.receipt_account_name}`
        : "—",
    ],
    ["Received on", when(payment.payment_date)],
    ["Recorded by", payment.recorded_by_name || "—"],
  ];
  const totals: [string, string, string][] = [
    ["Applied to invoices", money(payment.allocated_amount), "text-gray-900"],
    ["Held as student credit", money(payment.unallocated_amount), Number(payment.unallocated_amount) > 0 ? "text-amber-700" : "text-gray-900"],
    ["Refunded", money(payment.refunded_amount), "text-gray-900"],
    ["Still refundable", money(payment.refundable_amount), "text-gray-900"],
  ];

  return (
    <Modal
      isOpen
      size="3xl"
      title={title}
      onClose={onClose}
      footer={
        <>
          {onRequestRefund && (
            <button
              type="button"
              onClick={onRequestRefund}
              className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <RotateCcw className="h-4 w-4" /> Request refund
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-outline">
            Close
          </button>
          {onDownloadReceipt && (
            <button type="button" onClick={onDownloadReceipt} className="btn-primary">
              <Download className="mr-1.5 h-4 w-4" /> Download receipt
            </button>
          )}
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Amount received</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">{money(payment.amount)}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
            <span className={`h-1.5 w-1.5 rounded-full ${PAYMENT_STATUS_DOTS[payment.payment_status] ?? "bg-gray-400"}`} />
            {paymentStatusLabel(payment.payment_status)}
          </span>
        </div>

        {refundPending && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            A refund request for this receipt is waiting for approval.
          </p>
        )}

        <dl className="grid gap-x-6 rounded-lg border border-gray-200 px-4 py-1 sm:grid-cols-2">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="text-right text-sm font-medium text-gray-900 break-words">{value}</dd>
            </div>
          ))}
        </dl>

        <div className="grid gap-2 sm:grid-cols-4">
          {totals.map(([label, value, tone]) => (
            <div key={label} className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`mt-0.5 text-sm font-semibold tabular-nums ${tone}`}>{value}</p>
            </div>
          ))}
        </div>

        <section aria-labelledby="payment-allocations-heading">
          <h4 id="payment-allocations-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Where the money went
          </h4>
          {payment.allocations?.length ? (
            <div className="sheet-wrap mt-2 max-h-none">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Applied on</th>
                    <th>By</th>
                    <th className="text-right">Amount (UGX)</th>
                    <th className="text-right">Refunded</th>
                    {onReallocate && <th className="text-right">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {payment.allocations.map((allocation) => (
                    <tr key={allocation.id}>
                      <td className="font-mono text-xs text-gray-800">
                        {allocation.invoice_number}
                        {allocation.reason && <span className="block font-sans text-[11px] text-gray-500">{allocation.reason}</span>}
                      </td>
                      <td className="text-gray-600">{when(allocation.allocated_at)}</td>
                      <td className="text-gray-600">{allocation.allocated_by_name || "—"}</td>
                      <td className="text-right tabular-nums text-gray-900">{Number(allocation.active_amount).toLocaleString()}</td>
                      <td className="text-right tabular-nums text-gray-600">
                        {Number(allocation.refunded_amount) ? Number(allocation.refunded_amount).toLocaleString() : "—"}
                      </td>
                      {onReallocate && (
                        <td className="text-right">
                          {Number(allocation.refunded_amount) === 0 && (
                            <button
                              type="button"
                              onClick={() => onReallocate(allocation)}
                              className="rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                            >
                              Reallocate
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-1.5 text-sm text-gray-500">
              Not applied to any invoice yet. The money is held for {payment.student_name} and goes to their next invoice.
            </p>
          )}
        </section>

        {(payment.refund_reason || payment.refunded_at) && (
          <section aria-labelledby="payment-refund-heading">
            <h4 id="payment-refund-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Refund</h4>
            <p className="mt-1.5 text-sm text-gray-800">
              {payment.refunded_at && <>Refunded {when(payment.refunded_at)}. </>}
              {payment.refund_reason}
            </p>
          </section>
        )}

        {children}
      </div>
    </Modal>
  );
}
